<template>
	<v-app class="manager-app">
		<v-main primary>
			<v-navigation-drawer class="manager-header" v-model="drawer" :temporary="showToggle" :absolute="showToggle" :mobile-breakpoint="mobileBreakpoint">
				<manager-header :user="user" :logo="displayedLogo" :logo-link="logoLink" />

				<manager-drawer-group :tabs="standardTabs" :active-tab="activeTab" @activeTab="setActiveTab" v-if="standardTabs.length"></manager-drawer-group>
				<v-divider v-if="standardTabs.length" />
				<manager-drawer-group :tabs="studioTabs" title="Studio Menu" :active-tab="activeTab" @activeTab="setActiveTab" v-if="studioTabs.length"></manager-drawer-group>
				<v-divider v-if="studioTabs.length" />
				<manager-drawer-group :tabs="adminTabs" title="Admin Menu" :active-tab="activeTab" @activeTab="setActiveTab" v-if="adminTabs.length"></manager-drawer-group>
				<v-divider v-if="adminTabs.length" />

				<v-list class="manager-drawer-group" dense>
					<v-list-item v-for="menu in includingDefaultDrawerMenus" :key="menu.title" :href="menu.href" @click="menu.onClick" :class="menu.class || {}">
						<v-list-item-icon><v-icon>{{ menu.icon }}</v-icon></v-list-item-icon>
						{{ menu.title }}
					</v-list-item>
				</v-list>
			</v-navigation-drawer>

			<div class="main-content-wrapper">
				<v-app-bar color="headerSecondary" class="main-toolbar manager-toolbar" style="transform: initial;">
					<div class="manager-toobar-group" >
						<v-app-bar-nav-icon dark @click.stop="drawer = !drawer" v-if="showToggle" />
						<div class="toolbar-title">{{ title }}</div>
						<a v-if="helpLink" :href="activeTab.helpLink" target="_blank" class="help-link">
							<i class="help large circle icon" style="cursor: pointer !important" />
						</a>
						<i class="help large circle icon" style="cursor: pointer !important" v-else-if="titleHelp" @click="fireTutorial" />
						<i class="file video large circle icon" style="cursor: pointer !important" v-if="titleVideoTutorial" @click="openVideoTutorial" />
					</div>

					<manager-school-header v-if="showSchoolHeader" :plic-slug="plicSlug" :go-to-school-button="goToSchoolButton" :get-go-to-school-callback="getGoToSchoolCallback" :user-permissions="userPermissions" :no-projects-warning="noProjectsWarning" :org-role="orgRole" />

					<div class="manager-toobar-group right-toolbar manager-right-toolbar" >
						<component v-for="component in rightToolbarComponents" :key="component.template" :is="component"></component>
						<manager-login-as v-if="loginAsAnyone" />
						<switch-account :accounts="altAccounts" :plic-slug="plicSlug" @startLoading="loading = true" @stopLoading="loading = false" />
					</div>
				</v-app-bar>

				<v-container class="content main-content">
					<manager-tab v-for="tab in tabs" :key="tab.html" :tab="tab" :active="isActiveTab(tab)"></manager-tab>

					<div id="pageLoading" class="ui inverted dimmer" :class="{ active: loading }" style="z-index: 100000">
						<div class="ui text loader">Loading</div>
					</div>
				</v-container>
			</div>
		</v-main>

		<update-notification />
	</v-app>
</template>

<script>
import ManagerHeader from './ManagerHeader.vue';
import ManagerDrawerGroup from './ManagerDrawerGroup.vue';
import ManagerLoginAs from './ManagerLoginAs.vue';
import ManagerSchoolHeader from './ManagerSchoolHeader.vue';
// Remove after migrating tests
Vue.component('manager-school-header', ManagerSchoolHeader);
import ManagerTab from './ManagerTab.vue';
import UpdateNotification from '../common/UpdateNotification';
import { REBRAND_BANNER_MESSAGE } from '../../../vue/constants';
import SwitchAccount from './SwitchAccount.vue';
import getAccountsFromMe from './get-accounts-from-me';

export default {
	props: {
        drawerMenus: {
            default: function() {
                return [];
            }
		},
		standardTabs: {
			default: function() {
				return [];
			}
		},
		studioTabs: {
			default: function() {
				return [];
			}
		},
		adminTabs: {
			default: function() {
				return [];
			}
		},
		hiddenTabs: {
			default: function() {
				return [];
			}
		},
        changelog: {
            default: function() {
                return new $.Changelog({
                    changes: []
                });
            }
        },
        project: {},
        dbQueue: {
            default: null
        },
		showSchoolHeader: {
			default: false,
			type: Boolean
		},
		goToSchoolButton: {
			default: 'Go to School'
		},
		getGoToSchoolCallback: null,
		appLogo: {
			default: '/css/images/logo.png'
		},
		rightToolbarComponents: {
			default: function() {
				return [];
			}
		},
		noProjectsWarning: {
			default: 'No projects to display'
		}
	},
	data: function() {
		var activeTab = this.getDefaultActiveTab();
		if(activeTab) {
			$.activeTabPath = activeTab.html;
		}
		const mobileBreakpoint = 980;

		let drawerType = Math.min(window.innerWidth, window.outerWidth || window.innerWidth) > mobileBreakpoint ? 'permanent' : 'temporary';
		return {
			plicSlug: null,
			userPermissions: {},
			defaultWorkflowId: null,
			studioBranding: null,
			isLicenseExpiring: false,
			activeTab: activeTab,
			loading: true,
			drawerType,
			loginAsAnyone: false,
			user: {
				name: 'User Name'
			},
			returnLogin: false,
			orgRole: null,
			leaveTabCallbacks: {},
			status: null,
			drawer: drawerType === 'permanent',
			mobileBreakpoint,
			REBRAND_BANNER_MESSAGE,
			altAccounts: []
		};
	},
	computed: {
		title: function() {
			if(this.activeTab) {
				if(this.activeTab.title) {
					return this.activeTab.title;
				} else if(this.activeTab.name) {
					if(window.i18n) {
						let name = 'tabs.' + this.activeTab.name.toCamelCase();
						let label = window.i18n.t(name);
						if(name !== label) {
							return label;
						}
					}

					return this.activeTab.name;
				} else {
					if(window.i18n) {
						let name = 'tabs.' + this.activeTab.html.replace('.html', '').replace('.vue', '').toCamelCase();
						let label = window.i18n.t(name);
						if(name !== label) {
							return label;
						}
					}

					return this.activeTab.html.replace('.html', '').replace('.vue', '').replace(/([A-Z])/g, " $1");
				}
			} else {
				return 'Management';
			}
		},
		titleHelp: function() {
			return $.managementTabTutorials && this.activeTab && ($.managementTabTutorials[this.activeTab.html] || $.managementTabTutorials[this.activeTab.html + '#loaded']);
		},
		titleVideoTutorial: function() {
			return this.activeTab && this.activeTab.videoTutorial;
		},
		helpLink: function() {
			return this.activeTab && this.activeTab.helpLink;
		},
		displayedLogo: function() {
			if(this.studioBranding && this.studioBranding.logoUrl) {
				return this.studioBranding.logoUrl;
			} else {
				return this.appLogo;
			}
		},
		logoLink: function() {
			if(this.studioBranding) {
				return this.studioBranding.supportUrl;
			} else {
				return null;
			}
		},
		showToggle: function() {
			return this.drawerType != 'permanent';
		},
		tabs: function() {
			var tabs = [];

			$.merge(tabs, this.standardTabs);
			$.merge(tabs, this.studioTabs);
			$.merge(tabs, this.adminTabs);
			$.merge(tabs, this.hiddenTabs);

			return tabs;
		},
		includingDefaultDrawerMenus: function() {
            var me = this;
            var menus = [];

			if(this.status && ['minor', 'major', 'critical'].includes(this.status.status.indicator)) {
				menus.push({
                    title: this.status.status.description,
                    icon: 'warning',
                    href: this.status.page.url,
					class: 'system-outage',
					onClick: function() {}
                });
			}
            if(this.changelog.shouldShow()) {
                menus.push({
                    title: i18n.t('tabs.whatsNew'),
                    icon: 'new_releases',
                    onClick: function() {
                        me.changelog.show();
                    }
                });
            }

            if(this.returnLogin) {
                menus.push({
                    title: i18n.t('tabs.returnLogin'),
                    icon: 'eject',
                    onClick: function() {
                        me.loading = true;
    
                        $.ajax({
                            url: 'ajax/switchUser.php',
                            type: 'post',
                            data: {
                                userId: $.userPermissions['loginAs']
                            },
                            success: function () {
                                window.location = '/';
                            },
                            error: function() {
                                me.loading = false;
                                $.Alert('Error', 'Failed to log out');
                            }
                        });
                    }
                });
            }

            menus.push({
                title: i18n.t('tabs.signOut'),
                icon: 'exit_to_app',
                onClick: function() {
                    me.loading = true;

                    $.ajax({
                        url: 'ajax/logout.php',
                        success: function(data) {
                            // An Auth0 session outlives ours, so enhanced sign-in hands back a
                            // logout url that ends it and returns the browser here
                            window.location = data?.logoutUrl || '/';
                        },
                        error: function() {
                            me.loading = false;
                            $.Alert('Error', 'Failed to log out');
                        }
                    });
                }
            });

            return menus;
		},
		pageBackground: function() {
			if(this.activeTab && this.activeTab.background) {
				return '';
				// return 'url(css/images/' + this.activeTab.background + ')';
			} else {
				return '';
			}
		}
	},
	methods: {
		getDefaultActiveTab: function() {
			var currentUrl = window.location.href;
			var hashIndex = currentUrl.indexOf('#');

			var activeTab = this.standardTabs[0];
			if(hashIndex != -1) {
				var hashContents = currentUrl.substr(hashIndex + 1);
				if(hashContents) {
					this.standardTabs.forEach(function(tab) {
						if(tab.html == hashContents) {
							activeTab = tab;
						}
					});
					this.studioTabs.forEach(function(tab) {
						if(tab.html == hashContents) {
							activeTab = tab;
						}
					});
					this.adminTabs.forEach(function(tab) {
						if(tab.html == hashContents) {
							activeTab = tab;
						}
					});
				}
			}

			return activeTab;
		},
		isActiveTab: function(tab) {
			return this.activeTab == tab && !this.loading;
		},
		setActiveTab: function(tab) {
			var currentUrl = window.location.href;
			// Remove any existing #
			var target = this.tabs[0].html;
			if(currentUrl.indexOf('#') != -1) {
				var urlPart = currentUrl.substr(currentUrl.indexOf('#') + 1);

				// # = Schools.html
				if(urlPart) {
					target = urlPart;
				}
			}
			var oldTab = this.activeTab;
			this.activeTab = tab;

			if(target == tab.html && oldTab.html != tab.html) {
				if(oldTab.forceRefresh && $.schoolChangeManager) {
					$.schoolChangeManager.updateOnReturn(target, oldTab);
				}

				if(this.leaveTabCallbacks[oldTab.html]) {
					this.leaveTabCallbacks[oldTab.html]();
				}
			}
			this.updateHistoryState();
			$.activeTabPath = this.activeTab.html;

			if(this.drawerType === 'temporary') {
				this.drawer = false;
			}
		},
		updateHistoryState: function() {
			var currentUrl = window.location.href;
			// Remove any existing #
			if(currentUrl.indexOf('#') != -1) {
				currentUrl = currentUrl.substr(0, currentUrl.indexOf('#'));
			}

			var isVisibleTab = !this.hiddenTabs.includes(this.activeTab);

			var path = this.activeTab.html;
			var newUrl = currentUrl + '#', data = null;
			if(isVisibleTab) {
				newUrl = newUrl + path;
			} else {
				newUrl = window.location.href;
				data = {
					subTab: path
				};
			}

			// Don't try to update history during jasmine spec tests
			if(window.location.href.indexOf('?spec=') === -1) {
				// If we passed any url params, remove them
				var paramsIndex = newUrl.indexOf('?');
				if(paramsIndex != -1 && (newUrl.includes('jobId') || newUrl.includes('subTab'))) {
					newUrl = newUrl.substr(0, paramsIndex) + newUrl.substr(newUrl.indexOf('#'));
					window.history.replaceState(data, null, newUrl);
				} else if(window.location.href != newUrl || JSON.stringify(data) != JSON.stringify(window.history.state)) {
					window.history.pushState(data, null, newUrl);
				}
			}
		},
		setActiveTabByName: function(name) {
			var newTab = this.getTabByName(name);

			if(newTab) {
				this.setActiveTab(newTab);
			}
		},
		loadUserSettings: function() {
			var me = this;
			$.ajax({
				url: 'ajax/getUserDetails.php',
				dataType: 'json',
				success: function(data) {
					me.loadGlobalData(data);
					me.loadMyUser(data);
					me.loadOrganizations(data);
				},
				error: function(jqXHR) {
					if(jqXHR.status == 401) {
						$.Alert('Error', 'You are not allowed to view this page', function() {
							window.location = '/login';
						});
					} else {
						$.Alert('Error', 'Error loading this page');
					}
				}
			});
		},
		loadGlobalData: function(data) {
			$.globalData = {};
			$.globalPermissionData = data;

			this.user = $.CurrentUser = data['currentUser'];
			this.userPermissions = $.userPermissions = data.permissions || data.userPermissions;
			$.studioSettings = data['studioSettings'];
			$.projectSettings = data['projectSettings'];
			$.userExtras = data.userExtras ? data.userExtras : {};
			this.plicSlug = $.plicSlug = data['plicSlug'];
			$.isManager = data.isManager;
			$.isCustomer = data.isCustomer;

			$.PlicToken = data.plicToken;
			$.PlicAccountId = data.plicAccountId;
			if(window.setupAxiosAuthToken && data.plicToken) {
				window.setupAxiosAuthToken(data.plicToken, data.plicAccountId);
			}
			$.PlicUrl = data.plicUrl;
			$.PlicAccept = data.plicAccept;
			$.OrgId = data.orgId;
			$.OrgName = data.orgName;
			$.RootOrgId = data.rootOrgId;
			$.RootOrgName = data.rootOrgName;
			$.PlicOrgId = data.plicOrgId;
			$.UserPlicOrgId = data.userPlicOrgId;
			$.PlicBugsnagAPIKey = data.plicBugsnagAPIKey;
			this.orgRole = $.OrgRole = data.role;
			$.AppName = data.appName;
			$.DisplayAppName = data.displayAppName ?? $.AppName;
			$.AppLicenseUsed = data.appLicenseUsed;
			$.UserRank = data.rank;
			$.UserRole = data.userRole;
			if(data.defaultWorkflowId) {
				this.defaultWorkflowId = $.defaultWorkflowId = data.defaultWorkflowId;
			}
			if(data.welcomeEmailSent === null) {
				$.ajax({
					url: 'ajax/sendWelcomeEmail.php'
				});
			}

			if(data.masks && $.appendToGlobalSVGDefinition) {
				$.AvailableMasks = data.masks;

				// Append mask definition to body
				var defs = [];
				for (var i = 0; i < $.AvailableMasks.length; i++) {
					var mask = $.AvailableMasks[i];
					if (mask.definition) {
						// If %ID% is defined, then replace it is a clipPath
						if (mask.definition.indexOf('%ID%') != -1) {
							mask.definition = mask.definition.replace('%ID%', 'Mask-' + mask.id);
							defs.push(mask.definition);
						}
					}
				}

				$.appendToGlobalSVGDefinition(defs);
			}

			if(data.addStudioBranding) {
				$.studioBranding = this.studioBranding = data.addStudioBranding;
			}

			if(data.studioSettingOrgs) {
				$.studioSettingOrgs = data.studioSettingOrgs;
			}

			if($.GoogleFontList) {
				var studioFonts = $.getStudioSetting('studioGoogleFonts', '');
				if(studioFonts) {
					studioFonts = studioFonts.split(',');

					window.loadGoogleFontsList(studioFonts);
				}
			}
			if(data.customDictionary) {
				$.CustomDictionary = data.customDictionary;
			}
			if(data.projectTemplates) {
				$.ProjectTemplates = data.projectTemplates;

				if(!$.projectTypeFieldSets) {
					$.projectTypeFieldSets = {};
				}
				
				data.projectTemplates.forEach(function(template) {
					template.projectTypes.forEach(function(projectType) {
						var editableFields = template.editableFields.map(function(editableField) {
							return $.extend(true, {}, editableField);
						});

						if($.projectTypeFieldSets[projectType]) {
							$.projectTypeFieldSets[projectType].editableFields = editableFields;
						} else {
							$.projectTypeFieldSets[projectType] = {
								editableFields: editableFields
							};
						}
					});
				});

				if($.projectSettings) {
					let projectType = $.projectSettings.find(function(setting) {
						return setting.name === 'projectType';
					});
					if(projectType) {
						let extras = projectType.extras;
						if(typeof extras === 'string') {
							extras = JSON.parse(extras);
							data.projectTemplates.forEach(template => {
								template.projectTypes.forEach(type => {
									if(!extras.options.includes(type)) {
										extras.options.push(type);
									}
								});
							});
							projectType.extras = JSON.stringify(extras);
						}
					}
				}
			}

			// TODO: Refactor into firing an event
			if(window.onLoadGlobalData) {
				window.onLoadGlobalData(data);
			}
			if(window.onLoadAppGlobalData) {
				window.onLoadAppGlobalData(data);
			}
		},
		loadMyUser: function(data) {
			$.plicLicenseFeatures = {};
			$.plicLicenseSlugs = [];

			var me = this;
			$.plicAPI({
				method: 'users/me',
				params: {
					include_licenses: {
						plic_app_id: $.plicSlug,
						filter_sub_orgs: true
					},
					include_plic_apps: true,
					include_accounts: true
				},
				type: 'GET',
				success: (userData) => {
					let myUser = userData.user;

					// This can happen if the user is somehow logged in with a PLIC App token
					if(!myUser) {
						$.ajax({
							url: 'ajax/logout.php',
							success: function(data) {
								window.location = data?.logoutUrl || '/';
							},
							error: function() {
								$.Alert('Error', 'Failed to logout');
								$('#logoutLoading').removeClass('active');
							}
						});
						return;
					}

					let currentAccount = userData.accounts.find(account => account.id === userData.current_account_id);
					let currentOrganization = userData.organizations.find(org => org.id === currentAccount.organization_id);
					$.plicLicenses = myUser.licenses;

					// This also returns sub-licenses, so we want to filter on the master license where one exists
					$.plicLicense = null;
					for(var i = 0; i < myUser.licenses.length; i++) {
						var license = myUser.licenses[i];
						if(!$.plicLicense || !license.master_plic_app_license_id) {
							$.plicLicense = license;
						}
					}

					if($.plicLicense) {
						$.plicLicense.feature_flags.forEach(function(feature) {
							$.plicLicenseFeatures[feature.plic_app_feature_slug] = feature.enabled;
						});

						if(myUser.manages_own_organization && $.plicLicense.organization_id == currentOrganization.id && moment($.plicLicense.expires_at) < moment().add(7, 'days') && !$.plicLicenseFeatures['is-demo']) {
							me.isLicenseExpiring = true;

							var expiringIn = moment().to(moment($.plicLicense.expires_at));
							let header = 'License Expiring Soon!';
							let tooltipPrefix = 'Expiring ';
							let alert = 'is about to expire';
							if(moment($.plicLicense.expires_at) < moment()) {
								header = 'License Expired!';
								tooltipPrefix = 'Expired ';
								alert = 'expired';
							}
							me.rightToolbarComponents.push({
								template: '<b class="header item" data-tooltip="' + tooltipPrefix + expiringIn +'" data-position="bottom center" style="color: yellow"><i class="red heartbeat icon"></i>' + header + '</b>'
							});

							if(window.alertify) {
								window.alertify.error('Your license ' + alert + ' ' + expiringIn + '. Please contact PhotoLynx support if you plan on extending your license.');
							}
						}
					}
					$.plicLicenseSlugs = myUser.active_plic_app_slugs;
					$.activePlicApps = myUser.plic_apps;
					if($.activePlicApps) {
						$.activePlicApps.caseInsensitiveSort('name');
					}

					// No account users could load without this
					// SubAccount users do not have a plicUserId
					if(data.currentUser.plicUserId) {
						this.altAccounts = getAccountsFromMe(userData);
					}

					me.checkOtherTabsLoggedIn(myUser.id + '-' + currentOrganization.id);
					if(window.onLoadLicense) {
						window.onLoadLicense($.plicLicense);
					}

					if($.UserPlicOrgId && currentOrganization.id != $.UserPlicOrgId) {
						me.refreshSession();
					} else {
						me.finishLoading(data);
					}
				},
				error: function(jqXHR) {
					if(jqXHR.status === 404 || jqXHR.status === 401) {
						me.loading = false;
						$.disableAjaxErrorReporting = true;
						$.Alert('Error', 'Your user has been deleted or is not allowed to access this page.  Please try logging in again.', function () {
							$.ajax({
								url: 'ajax/logout.php',
								success: function(data) {
									window.location = data?.logoutUrl || '/login';
								},
								error: function() {
									$.Alert('Error', 'Failed to logout');
									$('#logoutLoading').removeClass('active');
								}
							});
						});
					} else {
						me.loading = false;
						$.disableAjaxErrorReporting = true;
						$.Alert('Error', 'Failed to load user information.  Please try again.  If this problem continues, please try logging out and back in again.', function () {
							window.location = '/';
						});
					}
				}
			});
		},
		loadOrganizations: function() {
			// Just do a full org refresh every 1 in 10 calls
			// This is the only way to force refresh the parent org linkage if schools are changed
			if($.isManager && $.userPermissions.allowViewing && Math.random() > 0.9) {
				$.proxyAjax({
					url: 'ajax/getOrganizations.php',
					type: 'GET',
					dataType: 'json'
				});
			}
		},
		refreshSession: function() {
			var me = this;
			$.ajax({
				url: 'ajax/refreshSession.php',
				type: 'post',
				dataType: 'json',
				success: function() {
					window.location.reload();
				},
				error: function(xhr) {
					me.loading = false;

					var message = 'Failed to refresh user session';
					try {
						var data = JSON.parse(xhr.responseText);
						var reason = data.reason;
						if(reason != 'Plic error' && reason != 'Authentication error' && reason != 'Error from PLIC') {
							message = reason;
						}
					} catch(e) {
						console.error('Failed to parse response', e);
					}

					$.Alert('Error', message);
				}
			});
		},
		finishLoading: function(data) {
			this.loading = false;
			this.setupLoginAs();
			this.loadDefaultWorkflow();
			// this.checkStatusPage();

			if($.getChangelog) {
				this.changelog.setChanges($.getChangelog());
			}
			this.changelog.showNew();

			if(window.onFinishLoadingManager) {
				window.onFinishLoadingManager(data);
			}
		},
		setupLoginAs: function() {
			this.returnLogin = !!$.userPermissions['loginAs'];
			this.loginAsAnyone = !!$.userPermissions['loginAsAnyone'];
		},
		handleWindowResize: function() {
			var isDesktop = Math.min(window.innerWidth, window.outerWidth || window.innerWidth) > this.mobileBreakpoint;
			if(isDesktop) {
				this.drawerType = 'permanent';
				this.drawer = true;
			} else {
				this.drawerType = 'temporary';
			}

			$.fn.dataTable.defaults.bAutoWidth = isDesktop;
			this.$nextTick(function() {
				$('.manager-app .segment.manager-tab table.dataTable').each(function() {
					$(this).css('width', '');
					// We want the auto sizing logic, unless we are mobile
					if($(this).is(':visible') && isDesktop) {
						$(this).dataTable().fnAdjustColumnSizing();
					}
				});
			});
		},
		stubManagementTabs: function() {
			var me = this;
			$.managementTabs = {
				tab: function(action, param) {
					switch(action) {
						case 'change tab':
							me.setActiveTabByName(param);
							break;
					}
				}
			}

			window.returnToMainTab = function() {
				var oldTab = me.activeTab;
				var currentUrl = window.getCurrentUrlPath();
				me.setActiveTabByName(currentUrl);

				if($.schoolChangeManager) {
					$.schoolChangeManager.updateOnReturn(currentUrl, oldTab);
				}
			};
			window.returnToPreviousTab = function() {
				window.history.back();
			};
			window.getCurrentUrlPath = function() {
				var currentUrl = window.location.href;
				// Remove any existing #
				var target = me.tabs[0].html;
				if(currentUrl.indexOf('#') != -1) {
					var urlPart = currentUrl.substr(currentUrl.indexOf('#') + 1);

					// # = Schools.html
					if(urlPart) {
						target = urlPart;
					}
				}
				return target;
			};

			window.setTabVisible = function(tabPath, visible) {
				var tab = me.getTabByName(tabPath);
				if(tab) {
					tab.visible = visible;
				}
			}

			window.registerForLeaveTab = function(tabName, callback) {
				me.leaveTabCallbacks[tabName] = callback;
			}
		},
		getTabByName: function(name) {
			var newTab = null;
			this.tabs.forEach(function(tab) {
				if(tab.html === name || tab.html.replace('.vue', '.html') === name) {
					newTab = tab;
				}
			});

			return newTab;
		},
		forceUpdateTab: function(name) {
			var tab = this.getTabByName(name);
			if(tab) {
				tab.version++;
			} else {
				console.error('Failed to find tab to update: ' + name);
			}
		},
		handlePopState: function(e) {
			var currentUrl = window.location.href;
			// Remove any existing #
			var target = this.tabs[0].html;
			if(currentUrl.indexOf('#') != -1) {
				var urlPart = currentUrl.substr(currentUrl.indexOf('#') + 1);

				// # = Schools.html
				if(urlPart) {
					target = urlPart;
				}
			}
			if(e && e.state && e.state.subTab) {
				var startTab = this.getTabByName(target);
				var subTab = this.getTabByName(e.state.subTab);
				if(!startTab || subTab.loaded) {
					target = e.state.subTab;
				}
			}

			if(target != this.activeTab.html) {
				this.setActiveTabByName(target);
			}
		},
		loadDefaultWorkflow: function() {
			if(!this.defaultWorkflowId) {
				return;
			}

			var me = this;
			$.plicAPI({
				method: 'organizations/' + $.PlicOrgId + '/workflows',
				type: 'GET',
				params: {
					filter: {
						tagged_plic_app_id: this.plicSlug
					},
					include_items: true
				},
				success: function (data) {
					var workflows = data.workflows;
					for (var i = 0; i < workflows.length; i++) {
						var workflow = workflows[i];

						workflow.workflow_items.numberSort('position');
						if (workflow.id == me.defaultWorkflowId) {
							$.defaultWorkflow = workflow;
						}
					}

					if(me.activeTab && $.schoolListManager) {
						$.schoolListManager.runDelayedListener(me.activeTab.html);
					}
				}
			});
		},
		fireTutorial: function() {
			$.runTutorial(this.activeTab.html, this.titleHelp, true);
		},
		openVideoTutorial: function() {
			var modal = $('<div class="ui basic large modal"><i class="close icon"></i></div>');
			var content = $('<div class="content">');
			var iframe = $(this.activeTab.videoTutorial);
			iframe.css({
				display: 'block',
				margin: '0 auto'
			});
			content.append(iframe);

			return modal
				.append(content)
				.modal({
					onShow: function() {
						var me = this;
						window.setTimeout(function() {
							var contentWidth = content[0].clientWidth - (parseFloat(window.getComputedStyle(content[0], null).getPropertyValue('padding-left').replace('px', '')) * 2);
							// Stop if we are not visible enough yet
							if(contentWidth < 10) {
								return;
							}
							var iframeWidth = iframe.getFloatAttribute('width');
							var iframeHeight = iframe.getFloatAttribute('height');
							var iframeAspectRatio = iframeWidth / iframeHeight;

							var newWidth = contentWidth;
							var newHeight = contentWidth / iframeAspectRatio;
							iframe.attr('width', newWidth);
							iframe.attr('height', newHeight);

							$(me).modal('refresh');
						}, 10);
					},
					onHidden: function() {
						$(this).remove();
					}
				})
				.modal('show');
		},
		checkOtherTabsLoggedIn: function(userId) {
			if(!window.BroadcastChannel) {
				return;
			}

			this.broadcastChannel = new BroadcastChannel('userLoggedIn');
			this.broadcastChannel.postMessage(userId)
			this.broadcastChannel.onmessage = function(event) {
				var otherUserId = event.data;
				if(userId != otherUserId) {
					$.Alert('Warning', 'You have logged in with a different user in another tab.  Please refresh this page.', function () {
						window.location.reload();
					});
				}
			};
		},
		checkStatusPage: function() {
			// Don't really want to be showing this error for all of the low level users
			if(!$.isManager || !$.userPermissions.allowViewing) {
				return;
			}

			var me = this;
			// Go to https://status.plic.io/api for more information
			$.proxyAjax({
				url: 'https://status.plic.io/api/v2/summary.json',
				success: function(data) {
					var incident = data.incidents[0];
					if(!incident) {
						return;
					}
					var matchingSlug = $.plicSlug.replace('plic-', '');
					var matchingComponent = incident.components.find(function(component) {
						return component.name.toLowerCase().includes(matchingSlug);
					});
					if(!matchingComponent) {
						return;
					}

					me.status = data;

					if(['major', 'critical'].includes(data.status.indicator)) {
						window.userAlert('error', 'We are currently experiencing a major system outage.  Please visit ' + data.page.url + ' for more information');
					}
				}
			});
		}
	},
    mounted: function() {
		this.stubManagementTabs();
		this.loadUserSettings();
		window.addEventListener('resize', this.handleWindowResize);
		window.addEventListener('popstate', this.handlePopState);

		$.fn.dataTable.defaults.bAutoWidth = Math.min(window.innerWidth, window.outerWidth) > this.mobileBreakpoint;

		if(window.alertify) {
			$.setupAlertify(null);
		}

		window.userAlert = function(type, message) {
			if(window.alertify) {
				window.alertify[type](message);
			}
		};
	},
	beforeDestroy: function () {
		window.removeEventListener('resize', this.handleWindowResize);
		window.removeEventListener('popstate', this.handlePopState);

		if(this.broadcastChannel) {
			this.broadcastChannel.close();
			this.broadcastChannel = null;
		}
	},
	components: {
		ManagerHeader,
		ManagerDrawerGroup,
		ManagerLoginAs,
		ManagerSchoolHeader,
		ManagerTab,
		SwitchAccount,

		UpdateNotification
	}
};
</script>

<style>
	html {
		overflow: auto !important;
	}
	html, body, .v-application {
		height: 100%;
		height: 100vh;
		height: -webkit-fill-available;
	}

	.v-application {
		min-height: 100vh;
	}
	.v-application > .v-application--wrap {
		min-height: 100vh !important;
		min-height: -webkit-fill-available !important;
	}

	.v-application .v-navigation-drawer__content .app-logo {
		max-height: 90%;
		margin-right: 1em;
	}

	.v-application .app-content {
		text-align: center;
	}

	.v-application.ui.modal {
		height: initial;
		min-height: initial;
	}

	.app-content > .layout:nth-child(2) {
		display: none;
	}
	.fade-enter-active, .fade-leave-active {
		transition-property: opacity;
		transition-duration: 0.3s;
	}

	.fade-enter-active {
		transition-delay: 0.3s;
	}

	.fade-enter, .fade-leave-active {
		opacity: 0
	}
</style>

<style>
.manager-app .v-main__wrap {
	display: flex;
}
/* Vuetify has breakpoints to give max-width to .container */
.main-content {
	max-width: initial;
}
.main-content-wrapper {
	flex: 1;
	overflow: hidden;
}
.manager-app .main-content > .content {
	height: 100%;
	display: flex;
	flex-direction: column;
}

.manager-app .content > .ui.segment {
	flex: 0 1 auto;
	min-height: 80vh;
	margin-top: 0;
}

.ui.modals, .fancybox-overlay.fancybox-overlay-fixed, .ui.visible.popup {
	z-index: 10001;
}

.manager-toolbar.v-toolbar {
	contain: initial;
}
.manager-toolbar .v-toolbar__content {
	z-index: 100;
}
/* Stops changing tabs changing size of sidebar slightly */
.manager-header.v-navigation-drawer {
	flex-shrink: 0;
}
.manager-header.v-navigation-drawer--temporary {
	z-index: 101;
}

.manager-toolbar .manager-school-header {
	color: white;
}
.manager-toolbar .manager-school-header {
	flex-basis: auto;
}

.manager-toolbar .manager-right-toolbar .manager-login-as input {
	width: 100%;
}

@media only screen and (max-width: 991px) {
	.manager-toolbar .manager-right-toolbar > b {
		display: none;
	}
}

@media only screen and (max-width: 600px) {
	.manager-toolbar i.green.help.icon {
		display: none;
	}
	.manager-toolbar .toolbar-title {
		display: none;
	}
}

.manager-toolbar .manager-toobar-group {
	flex: 1;

	display: flex;
	flex-direction: row;
	align-items: center;

	color: white;
}
.manager-toolbar .manager-toobar-group.right-toolbar {
	justify-content: end;
}
.manager-toolbar .toolbar-title {
	font-size: 1.25rem;
}

.manager-header .manager-drawer-group .v-list-item {
	font-size: 0.875rem;
}
.manager-header .manager-drawer-group .v-list-item__icon:last-of-type {
	margin-left: 0;
}

.main-content-wrapper .manager-toobar-group > .help-link {
	color: white;
}
</style>